/**
 * The authoring surface for Core's native WebMCP tools.
 *
 * A command author writes four small functions and never sees Chrome. The
 * runtime owns registration, the abort signal, JSON Schema generation,
 * argument validation, tenant resolution, the dirty-state veto, confirmation,
 * and output encoding.
 *
 * This module is the shared floor. `runtime.ts` and `brandBibleWriter.ts` both
 * depend on it and neither depends on the other, which is why the patch digest
 * lives here rather than in either of them.
 */
import type { z } from 'zod';

/**
 * A workspace website resolved from the per-tab selection. Minted only by
 * `runtime.requireScope`. Branded because a website-less workspace derives
 * null (useWorkspaceStore.ts:57) and every mutating service call throws
 * 'No tenant selected', so the null case must be impossible to forget.
 */
export type TenantId = string & { readonly __brand: 'TenantId' };

/** Brand Bible sections. There is no J; BB_LETTERS is the runtime truth. */
export type BbLetter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I';

/**
 * Content planner surfaces. The unit a dirty verdict is published for, exactly
 * as a `BbLetter` is for the Brand Bible.
 *
 * One per view rather than one per page, because selection and unsaved state
 * are per view: the calendar can hold an open day panel while the table holds
 * nothing, and a single 'planner' section would let either one veto the other.
 */
export type PlannerSection = 'calendar' | 'feed' | 'table';

/**
 * Blog editor surfaces. Two sections, because the editor holds two independent
 * buffers. `editor` is the live document — the toolbar, the canvas selection,
 * save and publish all act on the on-screen TipTap buffer through the same
 * handlers the operator uses, so an agent edit there is collaborative, never a
 * clobber. `editor-meta` is the right rail's metadata card, which holds its own
 * debounced unsaved state: an agent that PATCHes the same field while that
 * buffer is open would race the rail's save, which is exactly the collision the
 * dirty veto exists to catch. Keeping them separate means unsaved metadata vetoes
 * a metadata write without falsely vetoing a bold.
 */
export type EditorSection = 'editor' | 'editor-meta';

/** Anything a command can be scoped to, and anything a dirty verdict can name. */
export type Section = BbLetter | PlannerSection | EditorSection;

/**
 * What a command costs if it is wrong. The single input to the safety policy,
 * and the only source for Chrome's `readOnlyHint`. Never a function of the
 * arguments; a command whose danger varies by argument is two commands.
 */
export type Consequence =
  | 'reads'
  /** Writes a row that does not reach generated content. */
  | 'writes-config'
  /**
   * Writes a row that projects into every content generation prompt with no
   * verification gate. projection.py:284 makes only `claim` columns
   * verification-gated; `context` columns such as voice_summary, services_offered
   * and competitor notes project immediately.
   */
  | 'writes-generation-input'
  /**
   * Starts work that costs real money. Planner generation is the case: a
   * create or a restart enqueues a run whose spend is not recoverable by
   * undoing the row.
   */
  | 'spends-budget'
  /**
   * Reaches the client's public WordPress site. Strictly worse than
   * `writes-config`, which stops at our own row. `POST
   * /blog-creation/content/{id}/schedule` is this; `PATCH
   * /content-planner/schedule/{id}` is not.
   */
  | 'writes-live-site'
  /** Removes a row or its content irrecoverably. Reserved for wave two. */
  | 'destroys-data';

/** A field an agent may author, and how much latitude it has. */
export type Authorability =
  /** Free prose a model can draft well. */
  | 'prose'
  /** Derivable from a site or a few operator facts, but must be exact. */
  | 'fact'
  /** Id, URL, colour, number, or compliance fact. Never invented. */
  | 'exact';

/**
 * An expected, recoverable outcome. Exceptions are reserved for bugs, so a
 * command that cannot proceed returns one of these rather than throwing.
 */
export interface Refusal {
  readonly kind: 'refused';
  readonly because: string;
  /** Names the command that would unblock the agent, so refusals teach. */
  readonly remedy?: { readonly command: CommandName; readonly why: string };
  /** Attached when a selector matched more than one row. Replaces read tools. */
  readonly candidates?: readonly { readonly id: string; readonly label: string }[];
  readonly retryable?: boolean;
}

/**
 * Everything a command may say back to the agent.
 *
 * There is deliberately no slot shaped like an API response, and `facts` is
 * primitive-only rather than `unknown`. Primitive-only is necessary but not
 * sufficient, because secrets are also strings, so the writer capability
 * additionally has no dependency path to tenant settings.
 */
export interface Receipt {
  readonly summary: string;
  readonly facts?: Readonly<Record<string, string | number | boolean>>;
  /**
   * True when the backend's in-band projection failed after the write landed.
   * brand_bible_v2.py:119 returns this and the store currently discards it,
   * so surfacing it needs the store adapter widened. Tracked as an ask.
   */
  readonly projectionFailed?: boolean;
}

/** One field's change inside a `Diff`. */
export interface DiffChange {
  readonly field: string;
  readonly from: string | null;
  /**
   * The rendered new value, for the human who reads the confirmation. MUST be
   * produced by `renderPatchValue`.
   */
  readonly to: string;
  /**
   * The exact typed value this change writes. REQUIRED whenever that value is
   * not a string, omitted when it is.
   *
   * The authorization digest binds TYPES (`computePatchDigest`) while `to` is a
   * lossy human rendering, so a non-string change that omits this mints a
   * digest over the rendering while the writer sends the value. The two
   * disagree and the write refuses, which is fail-closed and loud rather than
   * an approval for `null` quietly authorizing `""`.
   */
  readonly value?: unknown;
  readonly authorability: Authorability;
}

/** The exact change a human is asked to approve. Rendered, never paraphrased. */
export interface Diff {
  readonly table: string;
  readonly rowId: string | null;
  readonly tenant: TenantId;
  readonly changes: readonly DiffChange[];
  /**
   * True when any changed field projects into generation prompts unverified.
   * The confirmation must say so in its own line.
   */
  readonly reachesGeneratedContent: boolean;
}

/**
 * A one-use authorization minted after the operator approves, consumed inside
 * the writer. Binds the approval to the exact target, so an approval for one
 * tenant and patch can never authorize another.
 *
 * The frontend cannot provide exactly-once delivery. There is no server
 * compare-and-swap on brand bible entries today, so `baseVersion` is a
 * best-effort read-before-write and a concurrent editor can still lose an
 * update. Widening the backend to accept `expected_version` is an open ask.
 */
export interface CommitAuthorization {
  readonly __brand: 'CommitAuthorization';
  readonly tenant: TenantId;
  readonly table: string;
  readonly rowId: string | null;
  readonly patchDigest: string;
  readonly baseVersion: string | null;
  readonly expiresAt: number;
}

/**
 * SEAM 12. The ONE rule for reading a row's base version.
 *
 * INVARIANT. The runtime that MINTS `CommitAuthorization.baseVersion` and every
 * writer that RE-CHECKS it must both read the row through this function, so the
 * mint and the check can never pick two different fields off the same row.
 *
 * A monotonic counter is preferred over a timestamp because a row can carry
 * both. A `BbEntry` has `version` AND `updated_at`, and a timestamp-first mint
 * checked against a counter refused every Brand Bible update with a spurious
 * "moved from <timestamp> to <n>". Timestamps stay as the fallback for tables
 * that have no counter.
 *
 * Read by name only, never by spreading the row.
 */
const VERSION_FIELDS = ['version', 'row_version', 'updated_at', 'updatedAt'] as const;

export function readBaseVersion(
  row: Readonly<Record<string, unknown>> | null | undefined,
): string | null {
  if (!row) return null;
  for (let i = 0; i < VERSION_FIELDS.length; i += 1) {
    const field = VERSION_FIELDS[i];
    if (field === undefined) continue;
    const value = row[field];
    if (typeof value === 'string' && value.length > 0) return value;
    if (typeof value === 'number') return String(value);
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* The patch digest                                                            */
/*                                                                             */
/* SEAM 1. This used to exist twice: an FNV-1a pair inside `runtime.ts` and an  */
/* async SHA-256 exported from `brandBibleWriter.ts`. The runtime mints         */
/* `CommitAuthorization.patchDigest` and the writer re-derives it to check the  */
/* binding, so two implementations means the check compares two different       */
/* numbers and EVERY WRITE REFUSES. There is now exactly one, here, because     */
/* both modules already depend on this one and neither may depend on the other. */
/* -------------------------------------------------------------------------- */

/**
 * Key-sorted JSON. `JSON.stringify` preserves insertion order, so the same
 * object built two ways would otherwise digest differently, and the merge
 * columns (`owner`, `socials`, `hours`) are exactly the object-valued fields
 * where that bites.
 *
 * Values JSON drops (undefined, function, symbol) and values it cannot encode
 * (bigint, non-finite number) render as `null` rather than throwing. The writer
 * refuses all of them by name before it ever gets here, so this is the floor
 * under that check and not a substitute for it.
 */
function canonicalJson(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'null';
  if (typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts: string[] = [];
    for (let i = 0; i < value.length; i += 1) parts.push(canonicalJson(value[i]));
    return `[${parts.join(',')}]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort();
    const parts: string[] = [];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const member = record[key];
      // JSON.stringify omits an undefined member rather than encoding it, so
      // omitting it here keeps the digest and the wire in agreement.
      if (member === undefined) continue;
      parts.push(`${JSON.stringify(key)}:${canonicalJson(member)}`);
    }
    return `{${parts.join(',')}}`;
  }
  return 'null';
}

/**
 * The human-readable rendering of a patch value.
 *
 * DERIVED FROM THE CANONICAL FORM, NOT A SECOND ONE. Every value except a
 * top-level string is exactly `canonicalJson`; a top-level string is returned
 * unquoted so a confirmation reads as prose rather than as JSON.
 *
 * It is deliberately NOT what an authorization binds to. Rendering is lossy on
 * purpose, because `null` and the four-character string "null" should both read
 * as null to a human, and an authorization bound to the rendering would
 * therefore authorize either one. `computePatchDigest` binds the typed form
 * instead, and `DiffChange.value` is how a non-string change carries that typed
 * form to the mint.
 */
export function renderPatchValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return canonicalJson(value);
}

/** FNV-1a 32-bit, seeded. Two seeds are concatenated for a 64-bit result. */
function fnv1a(input: string, seed: number): number {
  let hash = seed >>> 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash ^ input.charCodeAt(i)) >>> 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function hex8(value: number): string {
  return (value >>> 0).toString(16).padStart(8, '0');
}

/**
 * `<length>:<bytes>`. Nothing inside the bytes can forge the prefix, which is
 * what a separator character can never promise.
 */
function lengthPrefixed(part: string): string {
  return `${part.length}:${part}`;
}

/**
 * A binding checksum over a patch. Field-sorted, TYPE-PRESERVING, synchronous.
 *
 * Two properties, both load-bearing, and the previous implementation had
 * neither.
 *
 *  - TYPED. Every leaf is encoded by `canonicalJson`, so `null`, `""`, `true`
 *    and `"true"` are four different digests and an object never digests as the
 *    string containing its own JSON. Digesting the human rendering instead made
 *    one approval cover all of them.
 *  - UNFORGEABLE BOUNDARIES. Each field name and each encoded value is length
 *    prefixed rather than delimited. Control characters used to be the
 *    delimiter, so a value containing them forged a field boundary:
 *    `{a: "\u0001b\u0000y"}` produced the same pre-hash material as
 *    `{a: "", b: "y"}`.
 *
 * Synchronous on purpose. Nothing hostile sits on the other side of this, so it
 * is a change detector rather than a security digest, and `crypto.subtle`
 * (async, secure-context only) would buy nothing while forcing every caller
 * that spends an authorization to become async.
 */
export function computePatchDigest(patch: Readonly<Record<string, unknown>>): string {
  const fields = Object.keys(patch).sort();
  // The field count is part of the material too, so a patch can never digest as
  // a prefix of a longer one.
  let canonical = lengthPrefixed(String(fields.length));
  for (let i = 0; i < fields.length; i += 1) {
    const field = fields[i];
    if (field === undefined) continue;
    canonical += lengthPrefixed(field) + lengthPrefixed(canonicalJson(patch[field]));
  }
  return hex8(fnv1a(canonical, 0x811c9dc5)) + hex8(fnv1a(canonical, 0x9e3779b9));
}

/**
 * The exact typed patch a diff describes, ready to digest.
 *
 * `change.value` wins over `change.to`, and that is the whole point: `to` is the
 * human rendering while the digest binds types. A change that omits `value` is a
 * string change, where the rendering and the value are the same bytes.
 */
export function patchFromDiff(diff: Diff): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (let i = 0; i < diff.changes.length; i += 1) {
    const change = diff.changes[i];
    if (!change) continue;
    patch[change.field] = change.value === undefined ? change.to : change.value;
  }
  return patch;
}

/* -------------------------------------------------------------------------- */
/* Dirty state                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * SEAM 2. This used to be a bare `ReadonlySet<BbLetter>`, which has no way to
 * say "we do not know". The focus module had to encode unknown as an all-nine
 * sentinel set and identity-compare against it, which is a workaround that any
 * caller can silently get wrong by treating the set as a plain answer.
 *
 * Three states, named. `unknown` is not `clean`: a page that never published is
 * unread, not verified empty, and a write on that basis can silently overwrite
 * an operator's open buffer.
 */
export type DirtyState =
  /** Positively known to hold nothing unsaved for this tenant. */
  | { readonly kind: 'clean' }
  /** Positively known, and these are the sections holding unsaved edits. */
  | { readonly kind: 'dirty'; readonly sections: ReadonlySet<Section> }
  /** Not established. Nothing published, another client, or an unreadable publish. */
  | { readonly kind: 'unknown'; readonly because: string };

/**
 * The fail-closed read of a `DirtyState`. Returns the refusal, or null when the
 * write may proceed.
 *
 * Exists so `unknown` cannot be mistaken for `clean` at a call site. Every
 * consumer goes through this rather than switching on `kind` itself.
 */
export function dirtyVeto(state: DirtyState, section: Section): Refusal | null {
  if (state.kind === 'clean') return null;

  if (state.kind === 'unknown') {
    return refuse(state.because, { retryable: true });
  }

  if (!state.sections.has(section)) return null;

  return refuse(
    `Section ${section} has unsaved edits open in your browser. Save or discard them yourself, ` +
      'then ask again. This tool cannot save, discard, or work around your local edits.',
    // No `remedy`, and the omission is load-bearing. `remedy` names a command
    // that would unblock the agent, and by design no such command exists. Only
    // the human can clear this, and the same call works once they do.
    { retryable: true },
  );
}

/** Query-only. Writing before consent is a type error, not a review comment. */
export interface ReadDeps {
  /**
   * Settles the store on `tenant` and returns the current row, or null.
   * Wraps awaitLoaded (brandBibleWizardStore.ts:587), which short-circuits when
   * loadedTenantId already matches and therefore does not refresh known values.
   */
  currentRow(table: string, tenant: TenantId, id?: string): Promise<Readonly<Record<string, unknown>> | null>;

  /** Resolves true only after the wizard store has settled on `tenant`. */
  brandBibleLoaded?(tenant: TenantId): Promise<boolean>;

  /**
   * Whether the operator is holding unsaved edits. A conflict is a hard veto,
   * and so is an unestablished answer. Read it through `dirtyVeto`.
   */
  dirtySections(tenant: TenantId): DirtyState;

  /**
   * SEAM 5, planner read. The id source every planner write depends on: a
   * write tool may only act on an id this returned, so the model cannot
   * invent one. Ranges are resolved by `plannerDates.ts` before they arrive
   * here, so this receives absolute instants and never `"today"` or `YYYY-MM`.
   *
   * Filters are not optional sugar. Selection is per browser tab and a bare
   * list is ~60 rows, which an agent pages through badly, so status and title
   * narrowing is what keeps one read under the 1.5K output ceiling.
   */
  findArticles?(
    tenant: TenantId,
    query: Readonly<{
      from?: Date;
      to?: Date;
      status?: readonly string[];
      titleContains?: string;
      limit?: number;
    }>,
  ): Promise<readonly PlannerArticleSummary[]>;

  /**
   * SEAM 12, keyword feed read. The id source for `planner_approve_keywords`,
   * which is otherwise an id consumer with no sibling list tool: an agent that
   * starts with nothing cannot reach a candidate list at all, because
   * `findArticles` returns articles and can neither match `primary_keyword`
   * nor report `duplicate_status`.
   *
   * Returns the two verdict fields as well as the id, so an agent can see WHY
   * a row would be refused before it tries. Filters exist for the same reason
   * they do on `findArticles`: a bare feed is hundreds of rows.
   */
  findKeywordCandidates?(
    tenant: TenantId,
    query: Readonly<{
      status?: readonly string[];
      textContains?: string;
      /** `YYYY-MM`, matched against the row's planning bucket. */
      targetMonth?: string;
      limit?: number;
    }>,
  ): Promise<readonly KeywordCandidateSummary[]>;

  /**
   * SEAM 13, blog editor read. The live document a `/editor/` page is showing:
   * its content id, what the operator has selected, and which formatting marks
   * are on. Read at call time from the page's published bridge, so it reflects
   * the buffer as it is now rather than a snapshot from resolve.
   *
   * Absent when no editor page is mounted, which is the honest answer for every
   * other route and makes each editor command refuse there rather than act on a
   * document that is not on screen.
   */
  editorContext?(tenant: TenantId): EditorContext | null;
}

/**
 * The projection of the on-screen blog editor an agent is allowed to see.
 * Primitive-only, like the planner summaries: the selected text, but never the
 * whole document body, and never a WordPress token.
 */
export interface EditorContext {
  /** The `blog_content` row id the editor is on. */
  readonly contentId: string;
  /** The current selection's plain text, empty string when nothing is selected. */
  readonly selectedText: string;
  /** True when the caret is collapsed (no range selected). */
  readonly selectionEmpty: boolean;
  /** Whether the bold mark is active across the current selection. */
  readonly boldActive: boolean;
  /** Whether the italic mark is active across the current selection. */
  readonly italicActive: boolean;
}

/**
 * The projection of a keyword-feed row an agent is allowed to see. Same rules
 * as `PlannerArticleSummary`: not the full row, primitive-only, and no
 * evidence blob. It carries the two gate fields deliberately, so a refusal is
 * predictable from a read rather than discovered by attempting a write.
 */
export interface KeywordCandidateSummary {
  readonly id: string;
  readonly keyword: string;
  readonly status: string;
  /** `clear` is the only value the server's approve gate accepts. */
  readonly duplicateStatus: string;
  /** `swap` means vetting flagged likely cannibalization. */
  readonly verdict: string | null;
  /** Planning bucket, `YYYY-MM`. Null rows get one filled on approval. */
  readonly targetMonth: string | null;
}

/**
 * The projection of an article an agent is allowed to see. Deliberately not
 * the full row: no content body, no credentials, no WordPress tokens, and
 * primitive-only for the same reason `Receipt.facts` is.
 */
export interface PlannerArticleSummary {
  readonly id: string;
  readonly title: string;
  readonly status: string;
  /** Calendar display date, `YYYY-MM-DD`, from the derived `scheduled_date`. */
  readonly calendarDate: string | null;
  /** True when a WordPress post already exists, published or draft. */
  readonly hasWordPressPost: boolean;
}

/**
 * The only thing that reaches the network. Deliberately not the Zustand store
 * and not a generic {table, payload} writer, so dangerous fields are
 * unreachable by construction rather than by an exclusion list every future
 * author has to rediscover.
 *
 * Absent by design, and load-bearing: no verify, no unverify, no public
 * approval, no delete, no client switch, no tenant settings, no legacy
 * whole-bible PUT. `verifyEntry` is excluded specifically because its default
 * false argument rewrites approved_for_public (brandBibleWizardStore.ts:658),
 * so re-verifying a public row silently demotes it.
 */
/**
 * PLANNER SEAMS ARE OPTIONAL, AND THAT IS THE SAFETY PROPERTY.
 *
 * Every planner seam below is `?`. A capability supplies only the seams it is
 * allowed to reach, so the Brand Bible writer — which supplies `commit` alone —
 * has no dependency path to publishing a client's site, exactly as it has none
 * to `verifyEntry`. Unreachable by construction, not by an exclusion list.
 *
 * The cost is that a planner command must check its seam is present and return
 * a `Refusal` when it is not, rather than assuming. That check is not
 * defensive noise: it is the runtime discovering it was handed the wrong
 * capability, which is a refusal ("this surface cannot publish"), not a bug.
 */
export interface WriteDeps {
  /**
   * Applies one atomic patch to one row.
   *
   * SEAM 4. Returns `Receipt | Refusal` rather than `Promise<Receipt>`. A
   * commit has many expected reasons to decline (an expired authorization, a
   * digest mismatch, a base version that moved, a forbidden key), and the
   * codebase convention is that refusals are VALUES and exceptions are for
   * bugs. Signalling those by throwing meant the runtime could not tell a real
   * refusal from a genuine fault, so a refusal degraded into the generic bug
   * message and the agent lost the one sentence that would let it recover.
   *
   * Still never resolves a falsy success. upsertEntry resolves null when the
   * store has no tenant (brandBibleWizardStore.ts:609); the writer converts
   * that into an explicit refusal rather than reporting a write that never
   * happened.
   */
  commit(
    auth: CommitAuthorization,
    patch: Readonly<Record<string, unknown>>,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 6. Moves the calendar display date ONLY.
   * `PATCH /content-planner/schedule/{id}`. Nothing reaches WordPress.
   *
   * Named separately from `scheduleWpPublish` on purpose: the two endpoints
   * read as one concept and are two systems, and a single `schedule` seam is
   * exactly how an agent asking to "move it to Friday" publishes a client's
   * article instead.
   */
  setCalendarDate?(
    auth: CommitAuthorization,
    articleId: string,
    /** `YYYY-MM-DD`, already validated by `plannerDates.ts`. */
    date: string,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 7. Syncs a WordPress draft and makes it go live at the given time.
   * `POST /blog-creation/content/{id}/schedule`. `writes-live-site`.
   */
  scheduleWpPublish?(
    auth: CommitAuthorization,
    articleId: string,
    publishAt: Date,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 8. `blogCreationService.create`. Spends budget.
   * `wordpressPublishMode` is REQUIRED, not defaulted: the create payload
   * currently omits it, so the backend falls back to a stored value and
   * "none" becomes an accident rather than a request.
   */
  createArticle?(
    auth: CommitAuthorization,
    draft: Readonly<{
      title: string;
      keyword?: string;
      calendarDate?: string;
      wordpressPublishMode: 'none' | 'draft';
    }>,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 9. `blogCreationService.publish`. `writes-live-site`.
   *
   * MUST NOT await the pre-publish gate. `publish:549` and `batchPublish:706`
   * block on a human click in `PrePublishGateProvider`, so a tool that awaits
   * it does not fail, it hangs forever. Gate violations are evaluated in
   * `resolve` and returned as a `Refusal`; by the time this is called the
   * article is already known to be publishable. Never routes to `autoPublish`,
   * which has no gate at all.
   */
  publishArticle?(
    auth: CommitAuthorization,
    articleId: string,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 10. `blogCreationService.regenerate`. Spends budget.
   *
   * Both arguments are REQUIRED because the service omits them and the
   * backend then replays the row's stored writer and publish mode. That is
   * the defect that put a wrong writer on a production article: the page's
   * `retryItem` sends them, the service does not.
   */
  regenerate?(
    auth: CommitAuthorization,
    articleId: string,
    options: Readonly<{
      modelOverride: string;
      wordpressPublishMode: 'none' | 'draft';
    }>,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 11. Keyword feed approval. `writes-config`.
   * The server gate needs `approved` AND `duplicate_status = 'clear'`, so a
   * candidate still marked duplicate is refused in `resolve`, not here.
   */
  approveKeyword?(
    auth: CommitAuthorization,
    keywordIds: readonly string[],
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 14, blog editor formatting. Toggles bold or italic on the current
   * selection of the live TipTap document, exactly as the toolbar button does.
   * `writes-config`: it mutates only the in-memory buffer, never persists, and
   * is undone with one Ctrl+Z. Nothing reaches our DB or the client site.
   */
  editorFormat?(
    auth: CommitAuthorization,
    mark: 'bold' | 'italic',
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 15, blog editor save. Persists the current buffer to the draft row via
   * the same `saveNow` the Save button calls. `writes-config`: it lands in our
   * `blog_content` row and never reaches the client's live WordPress site.
   */
  editorSaveDraft?(auth: CommitAuthorization): Promise<Receipt | Refusal>;

  /**
   * SEAM 16, blog editor canonical. Sets the canonical URL through the same
   * `blogCreationService.patchContent` the rail form calls. `writes-config`:
   * it edits our row's metadata; it reaches the public page only if and when
   * the article is later published.
   */
  editorSetCanonical?(
    auth: CommitAuthorization,
    canonicalUrl: string,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 17, blog editor publish. Publishes the article LIVE to the client's
   * WordPress site via `blogCreationService.publish`. `writes-live-site`, the
   * heaviest editor consequence, and the reason it is its own seam rather than a
   * flag on save.
   */
  editorPublishCms?(auth: CommitAuthorization): Promise<Receipt | Refusal>;

  /**
   * SEAM 18, blog editor JSON-LD. Sets the authored schema description through
   * the same `blogCreationService.updateAuthoredSchema` the rail's structured-data
   * card calls — it lands in `generation_metadata.postgen_schema.fields` with the
   * edited-guard stamp. `writes-config`: our row's schema metadata, live only when
   * published.
   */
  editorSetJsonldDescription?(
    auth: CommitAuthorization,
    description: string,
  ): Promise<Receipt | Refusal>;

  /**
   * SEAM 19, blog editor Open Graph image. Sets the OG / social-share image URL
   * override through the same `blogCreationService.patchContent` the rail's image
   * card calls (`og_image_url`). `writes-config`: our row's metadata, live only
   * when published.
   */
  editorSetOgImage?(
    auth: CommitAuthorization,
    imageUrl: string,
  ): Promise<Receipt | Refusal>;
}

/**
 * One prefix per surface area. Widened from `bb_${string}` when the content
 * planner became the second surface; ticket 03 in `.scratch/webmcp-surface/`
 * settles the convention. Adding an area means adding a prefix here, which is
 * deliberately a visible edit rather than a `${string}` free-for-all.
 */
export type CommandName = `bb_${string}` | `planner_${string}` | `ed_${string}`;

export interface ResolveInput<A> {
  readonly args: A;
  readonly tenant: TenantId;
  readonly read: ReadDeps;
  readonly signal: AbortSignal;
}

/** Every member of a command except the two the safety policy discriminates on. */
interface CommandShape<A, T> {
  readonly name: CommandName;
  readonly section: Section;
  /** Under 500 characters. Chrome's guidance, which Chrome does not enforce. */
  readonly description: string;
  /** Chrome validates nothing, so this is the only enforced contract. */
  readonly args: z.ZodType<A>;

  /** Loose agent arguments to an exact target. Reads only. */
  resolve(input: ResolveInput<A>): Promise<T | Refusal>;

  /** Pure. The confirmation must be deterministic and must render the diff. */
  diff(target: T, tenant: TenantId): Diff;

  /** The only writing step. Reached only with a consumed authorization. */
  run(target: T, auth: CommitAuthorization, write: WriteDeps): Promise<Receipt | Refusal>;
}

/** A command that only reads. The one shape `exposeToEmjae: true` may inhabit. */
export interface ReadCommand<A = unknown, T = unknown> extends CommandShape<A, T> {
  readonly consequence: 'reads';
  /** Opt in to Emjae's tool list. Default off, and never settable on a mutating command. */
  readonly exposeToEmjae?: boolean;
}

/**
 * A command that mutates something. `exposeToEmjae` is typed `false` rather
 * than omitted so that opting a writer in is a compile error at the authoring
 * site with the flag underlined, instead of an excess-property error that
 * reads like the key was misspelled.
 */
export interface WriteCommand<A = unknown, T = unknown> extends CommandShape<A, T> {
  readonly consequence: Exclude<Consequence, 'reads'>;
  readonly exposeToEmjae?: false;
}

/**
 * A tool as an author declares it.
 *
 * A union rather than one interface because Emjae reads untrusted third-party
 * content, so a prompt-injected model reaching a write tool is the failure this
 * contract exists to make unreachable. A runtime check would be the wrong
 * enforcement: `installAgentSurface` cannot refuse a bad command without
 * disabling tools for an operator who did nothing wrong, and a filter that is
 * only a filter is one `as never` away from being bypassed. Declaring
 * `exposeToEmjae: true` beside any consequence other than `reads` therefore
 * fails to compile, and no code ships.
 *
 * `consequence` stays the sole discriminant, so it must still be a literal at
 * the authoring site. It always was; `annotationsFor` in `install.ts` already
 * depends on it exhaustively.
 */
export type Command<A = unknown, T = unknown> = ReadCommand<A, T> | WriteCommand<A, T>;

/**
 * Identity at runtime; exists so a command's argument type is inferred from its
 * own zod schema rather than restated.
 */
export function defineCommand<A, T>(c: Command<A, T>): Command<A, T> {
  return c;
}

/**
 * The existential a catalog stores commands as. Storage only.
 *
 * `Command<A, T>` is invariant enough in `args: z.ZodType<A>` that a concrete
 * `Command<{ entryId: string }, Target>` is not assignable to
 * `Command<never, never>`, which forced every lane to write a cast at its
 * `asCatalog` call site. Casts at a boundary are exactly where a wrong shape
 * gets in, so the existential lives here instead.
 *
 * Neither `any` reaches a handler. The catalog is read only for a command's
 * descriptor fields (`name`, `section`, `description`, `consequence`, `args`,
 * `exposeToEmjae`)
 * and handed straight back to `createExecutor`, which re-binds `A` and `T` from
 * the command's own zod schema. Argument typing is enforced at authoring time
 * by `defineCommand` and at call time by `args.safeParse`, not by this alias.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- existential for heterogeneous catalog storage; see above.
export type AnyCommand = Command<any, any>;

/** Commands by name. What `installAgentSurface` registers. */
export type CommandRecord = Readonly<Record<CommandName, AnyCommand>>;

/** What `asCatalog` returns: the record, plus every name it had to drop. */
export interface CatalogBuild {
  readonly catalog: CommandRecord;
  /** Names that appeared more than once. Empty on a well-formed catalog. */
  readonly duplicates: readonly string[];
}

/**
 * Keys the catalog by each command's own name and REPORTS duplicates rather
 * than throwing on them.
 *
 * NEVER THROWS, and that is the requirement. `catalog.ts` calls this at module
 * scope, so a throw here happens while the static import graph is still
 * evaluating, before Sentry, before the global handlers, before React renders,
 * and before `mountAgentSurface` exists to contain it. The operator gets the
 * loading shell instead of an app. A bad tool catalog must disable tools, never
 * disable Core, so the duplicate becomes a value the installer reads.
 *
 * The first command to claim a name keeps it, which is arbitrary and does not
 * matter: `mount.ts` installs NOTHING when `duplicates` is non-empty. Chrome's
 * asynchronous InvalidStateError for a duplicate name stays unreachable from
 * our own list either way.
 */
export function asCatalog(commands: readonly AnyCommand[]): CatalogBuild {
  const out: Record<CommandName, AnyCommand> = {};
  const duplicates: string[] = [];
  for (const command of commands) {
    if (command.name in out) {
      duplicates.push(command.name);
      continue;
    }
    out[command.name] = command;
  }
  return Object.freeze({
    catalog: Object.freeze(out),
    duplicates: Object.freeze(duplicates) as readonly string[],
  });
}

/**
 * The tool names Emjae's model is allowed to be shown, sorted.
 *
 * This exists because Chrome's `ToolAnnotations` is a closed dictionary of
 * three optional hints with no room for a custom key, and widening the ambient
 * declaration to smuggle one in would put a field in the type that Chromium
 * hard-`CHECK`s over. So the opt-in travels beside the surface rather than
 * inside it, and the Eve client filters `getTools()` against this list.
 *
 * The `consequence` test is redundant against the type and is kept anyway: a
 * catalog is stored as `AnyCommand`, which is where a cast could have put a
 * writer, and this is the last place before a tool name reaches a model.
 */
export function emjaeExposedToolNames(catalog: CommandRecord): string[] {
  return Object.values(catalog)
    .filter((command) => command.consequence === 'reads' && command.exposeToEmjae === true)
    .map((command) => command.name)
    .sort();
}

/** Optional detail on a refusal. Every member is omitted rather than defaulted. */
export interface RefusalOptions {
  readonly remedy?: Refusal['remedy'];
  readonly candidates?: Refusal['candidates'];
  readonly retryable?: boolean;
}

/**
 * SEAM 3. The one constructor for a refusal.
 *
 * This previously threw `not implemented`, so the runtime built refusals with a
 * private local helper and the focus module built them as object literals. A
 * refusal that throws is not a value, which is the exact inversion this type
 * exists to prevent.
 *
 * Members are omitted rather than set to undefined, so a refusal never carries
 * an explicit `retryable: undefined` that reads differently from its absence
 * under a `JSON.stringify` or a structural comparison.
 */
export function refuse(because: string, options?: RefusalOptions): Refusal {
  return {
    kind: 'refused',
    because,
    ...(options?.remedy ? { remedy: options.remedy } : {}),
    ...(options?.candidates ? { candidates: options.candidates } : {}),
    ...(options?.retryable === undefined ? {} : { retryable: options.retryable }),
  };
}

/** Narrows an unknown to a `Refusal`. Shared so the shape is checked one way. */
export function isRefusal(value: unknown): value is Refusal {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { readonly kind?: unknown }).kind === 'refused'
  );
}
